<template>
  <div class="ob" :class="{ 'is-busy': busy }">
    <div v-if="busy" class="ob-progress" aria-hidden="true"><i /></div>

    <header class="ob-top">
      <span class="ob-mark">Smartbooks</span>
      <button
        v-if="isAuthenticated"
        class="ob-text"
        type="button"
        :disabled="busy"
        @click="doLogout"
      >
        Sign out
      </button>
    </header>

    <main class="ob-main">
      <p class="ob-kicker">{{ kicker }}</p>
      <h1 class="ob-title">{{ title }}</h1>
      <p class="ob-lead">{{ lead }}</p>

      <p v-if="error" class="ob-error">{{ error }}</p>
      <p v-else-if="busy && isAuthenticated && !provisioned" class="ob-note">Opening your book…</p>

      <form v-if="phase === 'account'" class="ob-stack" @submit.prevent="createAccount">
        <label class="ob-field">
          Your name
          <input v-model="draft.name" type="text" autocomplete="name" required />
        </label>
        <label class="ob-field">
          Email
          <input v-model="draft.email" type="email" autocomplete="email" required />
        </label>
        <label class="ob-field">
          Password
          <input
            v-model="draft.password"
            type="password"
            autocomplete="new-password"
            minlength="8"
            required
          />
        </label>
        <button class="ob-primary" type="submit" :disabled="busy || !canRegister">
          Open a book
        </button>
        <button class="ob-ghost" type="button" :disabled="busy" @click="googleStart">
          Continue with Google
        </button>
        <router-link class="ob-link" to="/auth/login">I already have a book</router-link>
      </form>

      <div v-else-if="phase === 'verify'" class="ob-stack">
        <p class="ob-note">We sent a link to {{ draft.email || userEmail }}.</p>
        <button class="ob-primary" type="button" :disabled="busy" @click="confirmVerified">
          I’ve confirmed
        </button>
        <button class="ob-ghost" type="button" :disabled="busy" @click="resendVerify">
          Send it again
        </button>
      </div>

      <div v-else-if="phase === 'hello'" class="ob-stack">
        <button class="ob-primary" type="button" :disabled="busy" @click="go('shop')">
          Let’s set the shop
        </button>
      </div>

      <form v-else-if="phase === 'shop'" class="ob-stack" @submit.prevent="go('hours')">
        <label class="ob-field">
          Shop name
          <input
            v-model="draft.shopName"
            type="text"
            autocomplete="organization"
            placeholder="The shop people ask for"
            required
          />
        </label>
        <label class="ob-field">
          Town
          <input v-model="draft.town" type="text" placeholder="Optional" />
        </label>
        <p class="ob-label">What do you do?</p>
        <div class="ob-chips" role="radiogroup" aria-label="Trade">
          <button
            v-for="trade in trades"
            :key="trade.id"
            type="button"
            class="ob-chip"
            :class="{ 'is-on': draft.trade === trade.id }"
            @click="setTrade(trade.id)"
          >
            {{ trade.label }}
          </button>
        </div>
        <button class="ob-primary" type="submit" :disabled="!draft.shopName.trim()">
          Next
        </button>
      </form>

      <form v-else-if="phase === 'hours'" class="ob-stack" @submit.prevent="go('you')">
        <div class="ob-days">
          <button
            v-for="day in days"
            :key="day"
            type="button"
            class="ob-day"
            :class="{ 'is-on': draft.days[day] }"
            @click="draft.days[day] = !draft.days[day]"
          >
            {{ day.slice(0, 3) }}
          </button>
        </div>
        <div class="ob-hours">
          <label class="ob-field">
            Weekdays
            <span class="ob-pair">
              <input v-model="draft.weekday.start" type="time" />
              <input v-model="draft.weekday.end" type="time" />
            </span>
          </label>
          <label v-if="draft.days.saturday" class="ob-field">
            Saturday
            <span class="ob-pair">
              <input v-model="draft.saturday.start" type="time" />
              <input v-model="draft.saturday.end" type="time" />
            </span>
          </label>
          <label v-if="draft.days.sunday" class="ob-field">
            Sunday
            <span class="ob-pair">
              <input v-model="draft.sunday.start" type="time" />
              <input v-model="draft.sunday.end" type="time" />
            </span>
          </label>
        </div>
        <button class="ob-primary" type="submit" :disabled="!hasOpenDay">
          Next
        </button>
      </form>

      <form v-else-if="phase === 'you'" class="ob-stack" @submit.prevent="go('menu')">
        <label class="ob-field">
          Your name on the book
          <input v-model="draft.personName" type="text" autocomplete="name" />
        </label>
        <label class="ob-check">
          <input v-model="draft.takeBookings" type="checkbox" />
          I’ll take bookings
        </label>
        <button class="ob-primary" type="submit" :disabled="!canContinueYou">
          Next
        </button>
      </form>

      <form v-else-if="phase === 'menu'" class="ob-stack" @submit.prevent="go('ready')">
        <div v-for="row in draft.menu" :key="row.key" class="ob-menu">
          <label class="ob-check">
            <input v-model="row.on" type="checkbox" />
            {{ row.name }}
          </label>
          <label class="ob-mini">
            €
            <input v-model.number="row.price" type="number" min="0" step="1" />
          </label>
          <label class="ob-mini">
            <input v-model.number="row.duration" type="number" min="5" step="5" />
            min
          </label>
        </div>
        <button class="ob-primary" type="submit">Next</button>
      </form>

      <div v-else-if="phase === 'ready'" class="ob-stack">
        <ul class="ob-summary">
          <li><strong>{{ summary.shopName }}</strong> · {{ summary.trade }}</li>
          <li v-if="summary.town">{{ summary.town }}</li>
          <li>{{ summary.openDays.length }} days open</li>
          <li v-if="summary.takeBookings && summary.personName">
            {{ summary.personName }} on the chair
          </li>
          <li>{{ summary.menuCount }} services</li>
        </ul>
        <p class="ob-note">
          The book is ready. Every shop is €{{ saasPlan.price }} a month after
          the free days — Boxx Finglas is the only exception.
        </p>
        <button class="ob-primary" type="button" :disabled="busy" @click="finish">
          Continue to billing
        </button>
      </div>

      <div v-else-if="phase === 'billing'" class="ob-stack">
        <button class="ob-primary" type="button" :disabled="busy" @click="paySaas">
          Pay €{{ saasPlan.price }} / month
        </button>
        <button
          v-if="trialActive"
          class="ob-ghost"
          type="button"
          :disabled="busy"
          @click="enterApp"
        >
          Open the book — free days left
        </button>
        <button
          v-else-if="canOfferTrial"
          class="ob-ghost"
          type="button"
          :disabled="busy"
          @click="startTrial"
        >
          Start 14 days free
        </button>
      </div>

      <button
        v-if="canBack"
        class="ob-text ob-back"
        type="button"
        :disabled="busy"
        @click="back"
      >
        Back
      </button>
    </main>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ROSTER_DAYS } from "@/helpers/rosterDefaults";
import { errorMessage } from "@/helpers/bookingSearch";
import {
  ONBOARD_TRADES,
  captureVerifyToken,
  draftIsReadyToSave,
  emptyOnboardDraft,
  onboardState,
  onboardSummary,
  planOnboardPersist,
  starterMenu
} from "@/helpers/onboard";
import {
  canStartSaasTrial,
  hasActiveSubscriptionAccess,
  isSaasOwner,
  SAAS_PLAN
} from "@/helpers/saasBilling";

const STEPS = ["hello", "shop", "hours", "you", "menu", "ready"];

export default {
  name: "Onboard",
  data() {
    return {
      trades: ONBOARD_TRADES,
      days: ROSTER_DAYS,
      phase: "account",
      draft: emptyOnboardDraft(),
      busy: false,
      error: "",
      provisioned: false
    };
  },
  computed: {
    ...mapGetters({
      isAuthenticated: "isAuthenticated",
      isVerified: "isVerified",
      userId: "userId",
      userEmail: "userEmail",
      userClaims: "userClaims",
      shop: "shops/shop",
      userSettings: "userSettings",
      subscription: "subscription",
      billingBlocked: "billingBlocked",
      isSaasBilledTenant: "isSaasBilledTenant"
    }),
    saasShop() {
      if (this.isSaasBilledTenant) return true;
      return isSaasOwner({
        uid: this.userId,
        storeId: (this.userClaims && this.userClaims.storeId) || (this.shop && (this.shop._id || this.shop.storeId)),
        tenantId: this.userClaims && this.userClaims.tenantId,
        shop: this.shop
      });
    },
    saasPlan() {
      return SAAS_PLAN;
    },
    bookState() {
      return onboardState({
        shop: this.shop,
        settings: this.userSettings,
        userId: this.userId
      });
    },
    onboardNeeded() {
      return this.bookState === "needed";
    },
    kicker() {
      if (this.phase === "account") return "Smartbooks";
      if (this.phase === "verify") return "Check your email";
      if (this.phase === "hello") return "Your book";
      if (this.phase === "shop") return "The shop";
      if (this.phase === "hours") return "The hours";
      if (this.phase === "you") return "The chair";
      if (this.phase === "menu") return "The menu";
      if (this.phase === "billing") return "The plan";
      return "Ready";
    },
    title() {
      if (this.phase === "account") return "Open a book.";
      if (this.phase === "verify") return "Tap the link.";
      if (this.phase === "hello") return "Two quiet minutes.";
      if (this.phase === "shop") return "What’s the shop called?";
      if (this.phase === "hours") return "When are you open?";
      if (this.phase === "you") return "Who’s on the book?";
      if (this.phase === "menu") return "What do you sell?";
      if (this.phase === "billing") return "€25 a month.";
      return "You’re on.";
    },
    lead() {
      if (this.phase === "account") {
        return "The diary that looks after the chair.";
      }
      if (this.phase === "verify") {
        return "Come back here after you confirm. We’ll finish the shop.";
      }
      if (this.phase === "hello") {
        return "Name, hours, you, a couple of services. Then the book is live.";
      }
      if (this.phase === "shop") return "People will see this name.";
      if (this.phase === "hours") return "Tap the days. Times can change later.";
      if (this.phase === "you") return "Usually you. You can add people after.";
      if (this.phase === "menu") return "Switch off what you don’t do. Prices later too.";
      if (this.phase === "billing") {
        return this.billingLead;
      }
      return "You can change any of this in Settings, Employees, and Services.";
    },
    billingLead() {
      if (this.trialActive) {
        return `€${this.saasPlan.price} a month after your free days. Add a card now, or open the book while the trial is live.`;
      }
      if (this.canOfferTrial) {
        return `€${this.saasPlan.price} a month. One plan for every shop except Boxx Finglas. Start 14 days free, then a card is required.`;
      }
      return `This shop needs a live Smartbooks plan. €${this.saasPlan.price} a month, or the book stays locked.`;
    },
    hasOpenDay() {
      return this.days.some(day => this.draft.days && this.draft.days[day]);
    },
    canContinueYou() {
      if (this.draft.takeBookings === false) return true;
      return !!String(this.draft.personName || "").trim();
    },
    trialActive() {
      return (
        !!this.subscription &&
        !!this.subscription.trialPeriod &&
        hasActiveSubscriptionAccess(this.subscription)
      );
    },
    canOfferTrial() {
      return canStartSaasTrial(this.subscription);
    },
    canRegister() {
      return !!(
        this.draft.name.trim() &&
        this.draft.email.trim() &&
        this.draft.password.length >= 8
      );
    },
    canBack() {
      if (this.phase === "billing") return this.bookState !== "done";
      return STEPS.indexOf(this.phase) > 0;
    },
    summary() {
      return onboardSummary(this.draft);
    }
  },
  watch: {
    isAuthenticated() {
      this.routePhase();
    },
    isVerified() {
      this.routePhase();
    },
    billingBlocked() {
      this.routePhase();
    }
  },
  async created() {
    this.seedFromUser();
    const token = captureVerifyToken(this.$route);
    if (token) {
      try {
        await this.verifyUser({ token });
      } catch (error) {
        this.error = errorMessage(error, "Could not confirm that email.");
      }
    }
    if (this.$route.query.saas_checkout === "success") {
      await this.confirmSaasReturn();
      return;
    }
    if (this.$route.query.saas_checkout === "cancel" || this.$route.query.pay) {
      this.phase = "billing";
    }
    if (this.isAuthenticated) {
      await this.prepareBook();
    }
    this.routePhase();
  },
  methods: {
    ...mapActions([
      "register",
      "loginWithGoogle",
      "logout",
      "refreshUserClaims",
      "reloadCurrentUser",
      "verifyUser",
      "sendVerificationEmail",
      "fetchUserSettings",
      "updateUserSettings",
      "startSaasCheckout",
      "confirmSaasCheckout",
      "startSaasTrial",
      "fetchSubscription"
    ]),
    ...mapActions({
      fetchShop: "shops/fetchShop",
      updateShop: "shops/updateShop",
      fetchEmployees: "employees/fetchEmployees",
      createEmployee: "employees/createEmployee",
      fetchServices: "services/fetchServices",
      createService: "services/createService"
    }),
    seedFromUser() {
      const display = (this.$store.state.auth.userToken || {}).displayName || "";
      this.draft = emptyOnboardDraft({
        ...this.draft,
        name: this.draft.name || display,
        email: this.draft.email || this.userEmail || "",
        personName: this.draft.personName || display,
        shopName:
          this.draft.shopName ||
          (this.shop && this.shop.name && String(this.shop.name).indexOf("@") === -1
            ? this.shop.name
            : "")
      });
    },
    setTrade(id) {
      this.draft.trade = id;
      this.draft.menu = starterMenu(id);
    },
    go(phase) {
      this.error = "";
      this.phase = phase;
    },
    back() {
      if (this.phase === "billing") {
        this.phase = "ready";
        return;
      }
      const index = STEPS.indexOf(this.phase);
      if (index > 0) this.phase = STEPS[index - 1];
    },
    routePhase() {
      if (!this.isAuthenticated) {
        this.phase = "account";
        return;
      }
      if (!this.isVerified) {
        this.phase = "verify";
        return;
      }
      if (this.bookState === "unknown") {
        return;
      }
      if (this.bookState === "done") {
        if (this.saasShop && this.billingBlocked) {
          this.phase = "billing";
          return;
        }
        if (this.phase === "billing") return;
        this.$router.replace("/bookings");
        return;
      }
      if (this.phase === "account" || this.phase === "verify") {
        this.phase = "hello";
      }
    },
    async prepareBook() {
      this.busy = true;
      this.error = "";
      try {
        await this.waitForProvision();
        this.seedFromUser();
        this.routePhase();
      } catch (error) {
        this.error = errorMessage(error, "Could not open your book yet.");
      } finally {
        this.busy = false;
      }
    },
    async waitForProvision() {
      for (let i = 0; i < 20; i += 1) {
        try {
          await this.refreshUserClaims();
          if (this.userClaims && this.userClaims.storeId) {
            await this.fetchShop();
            await this.fetchUserSettings();
            try {
              await this.fetchSubscription();
            } catch (error) {
              /* billing gate waits until this succeeds */
            }
            if (this.shop && (this.shop._id || this.shop.storeId)) {
              this.provisioned = true;
              return;
            }
          }
        } catch (error) {
          this.error = errorMessage(error, "");
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      throw new Error("Your book is still opening. Wait a moment and refresh.");
    },
    async createAccount() {
      if (!this.canRegister || this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.register({
          name: this.draft.name.trim(),
          email: this.draft.email.trim(),
          password: this.draft.password
        });
        this.draft.personName = this.draft.personName || this.draft.name.trim();
        this.phase = "verify";
      } catch (error) {
        this.error = errorMessage(error, "Could not create the book.");
      } finally {
        this.busy = false;
      }
    },
    async googleStart() {
      this.busy = true;
      this.error = "";
      try {
        await this.loginWithGoogle();
        await this.prepareBook();
        this.routePhase();
      } catch (error) {
        this.error = errorMessage(error, "Google sign-in failed.");
        this.busy = false;
      }
    },
    async confirmVerified() {
      this.busy = true;
      this.error = "";
      try {
        const token = captureVerifyToken(this.$route);
        if (token) {
          await this.verifyUser({ token });
        } else {
          await this.reloadCurrentUser();
        }
        if (this.isVerified) {
          await this.prepareBook();
          return;
        }
        this.error = token
          ? "Still waiting on that email. Open the newest Smartbooks link in this browser, then tap I’ve confirmed."
          : "That confirm link never reached this page. Open the newest Smartbooks email here, then try again.";
      } catch (error) {
        this.error = errorMessage(error, "Could not check confirmation.");
      } finally {
        this.busy = false;
      }
    },
    async resendVerify() {
      this.busy = true;
      this.error = "";
      try {
        await this.sendVerificationEmail();
        this.$notify({ type: "success", text: "Link sent." });
      } catch (error) {
        this.error = errorMessage(error, "Could not send the email.");
      } finally {
        this.busy = false;
      }
    },
    async finish() {
      if (!draftIsReadyToSave(this.draft) || this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.persistBook(false);
        if (this.saasShop) {
          this.phase = "billing";
          this.busy = false;
          return;
        }
        this.$router.replace("/bookings");
      } catch (error) {
        const message = errorMessage(error, "Could not save the shop.");
        this.error = /insufficient permissions/i.test(message)
          ? "Could not save the shop. Refresh this page and tap Open the book again."
          : message;
        this.busy = false;
      }
    },
    checkoutUrls() {
      const origin =
        (typeof window !== "undefined" && window.location && window.location.origin) ||
        "https://app.smartbooks365.com";
      return {
        success_url: `${origin}/start?saas_checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/start?saas_checkout=cancel`
      };
    },
    async paySaas() {
      if (this.busy) return;
      this.busy = true;
      this.error = "";
      try {
        await this.startSaasCheckout(this.checkoutUrls());
      } catch (error) {
        this.error = errorMessage(error, "Could not open card checkout.");
        this.busy = false;
      }
    },
    async startTrial() {
      if (this.busy || !this.canOfferTrial) return;
      this.busy = true;
      this.error = "";
      try {
        await this.startSaasTrial();
        await this.fetchSubscription();
        this.enterApp();
      } catch (error) {
        this.error = errorMessage(error, "Could not start the free days.");
        this.busy = false;
      }
    },
    enterApp() {
      if (this.saasShop && this.billingBlocked) {
        this.phase = "billing";
        this.busy = false;
        return;
      }
      this.$router.replace("/bookings");
    },
    async confirmSaasReturn() {
      this.busy = true;
      this.error = "";
      try {
        const sessionId = this.$route.query.session_id;
        if (sessionId) {
          await this.confirmSaasCheckout(sessionId);
        }
        await this.fetchSubscription();
        this.enterApp();
      } catch (error) {
        this.phase = "billing";
        this.error = errorMessage(error, "Card went through, but we could not confirm it yet. Try again.");
        this.busy = false;
      }
    },
    async persistBook(skipped) {
      await this.refreshUserClaims();
      if (!this.userClaims || !this.userClaims.storeId || !this.userClaims.tenantId) {
        throw new Error("Your book is still opening. Wait a moment and try again.");
      }
      try {
        await this.fetchEmployees();
      } catch (error) {
        /* first-run shops may have no staff yet */
      }
      try {
        await this.fetchServices();
      } catch (error) {
        /* first-run shops may have no services yet */
      }

      const plan = planOnboardPersist({
        draft: this.draft,
        shop: this.shop,
        employees: this.$store.getters["employees/employeesList"] || [],
        services: this.$store.getters["services/servicesList"] || []
      });
      await this.updateShop(plan.nextShop);

      let resourceId = plan.reuseEmployeeId;
      if (plan.person) {
        await this.createEmployee(plan.person);
        const created = (this.$store.getters["employees/employeesList"] || []).find(
          row => row && row.name === plan.person.name
        );
        resourceId = created ? created._id : null;
      }

      for (let i = 0; i < plan.servicesToCreate.length; i += 1) {
        const row = plan.servicesToCreate[i];
        await this.createService({
          ...row,
          resources: resourceId ? [resourceId] : row.resources
        });
      }
      await this.markComplete(skipped);
    },
    async markComplete(skipped) {
      const settings = { ...(this.userSettings || {}) };
      const company = { ...(settings.company || {}) };
      company.name = this.draft.shopName.trim() || company.name;
      settings.company = company;
      settings.onboarding = {
        completedAt: Date.now(),
        version: 1,
        skipped: !!skipped
      };
      await this.updateUserSettings(settings);
    },
    async doLogout() {
      try {
        await this.logout();
      } finally {
        this.phase = "account";
      }
    }
  }
};
</script>
